<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { FairSearchBar } from '@/ui/patterns/FairSearchBar'
import { FairAiTip } from '@/ui/primitives/FairAiTip'
import { useImageSearch } from '@/features/search/store'
import FairHomeHeroBanner from './FairHomeHeroBanner.vue'
import {
  FAIR_HOME_HERO_TIP_KEYS,
  getFairHomeHeroHeaderSize,
  getFairHomeHeroLayout,
  getFairHomeHeroSearchDevice,
} from './fair-home-hero.data'
import { useFairHomeSearchNavigate } from './useFairHomeSearchNavigate'
import { useFairHomeSearchPanel } from './useFairHomeSearchPanel'
import './fair-home-hero.css'
import { FAIR_HOME_HERO_BG_URL } from './fair-home-lcp-assets'

const FairHomeSearchTutorialSheet = defineAsyncComponent(
  () => import('./FairHomeSearchTutorialSheet.vue'),
)

const { t } = useI18n()

const searchQuery = ref('')
const bannerPage = ref(1)
const isSearchTutorialOpen = ref(false)
const viewportWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : 1920,
)

const imageSearch = useImageSearch()
const { imageBase64Data } = storeToRefs(imageSearch)
const { navigateToSearch } = useFairHomeSearchNavigate()
const {
  history,
  suggestions,
  brands,
  suggestionsPending,
  handleSelectHistory,
  handleSelectSuggestion,
  handleRemoveHistory,
} = useFairHomeSearchPanel(searchQuery)

const photoUrl = computed(() => imageBase64Data.value ?? undefined)

const headerSize = computed(() => getFairHomeHeroHeaderSize(viewportWidth.value))
const layout = computed(() => getFairHomeHeroLayout(viewportWidth.value))
const searchDevice = computed(() =>
  getFairHomeHeroSearchDevice(viewportWidth.value),
)
const isCompact = computed(() => layout.value === 'compact')

const title = computed(() =>
  t(
    isCompact.value
      ? 'fair_home.hero.title_compact'
      : 'fair_home.hero.title_desktop',
  ),
)

const searchPlaceholder = computed(() =>
  t('fair_home.hero.search_placeholder'),
)

const searchButtonLabel = computed(() => t('fair_home.hero.search_button'))
const addPhotoLabel = computed(() => t('fair_home.add_photo.label'))
const deletePhotoLabel = computed(() => t('fair_home.add_photo.delete'))

const historyTitle = computed(() => t('home_page.search_history'))
const brandsTitle = computed(() => t('home_page.brands'))

const aiTipLabel = computed(() => t('fair_home.hero.ai_tip'))

const tips = computed(() =>
  FAIR_HOME_HERO_TIP_KEYS.map((key) => t(`fair_home.hero.tips.${key}`)),
)

const rootClass = computed(() => ({
  'fair-home-hero': true,
  'fair-home-hero--compact': isCompact.value,
  [`fair-home-hero--size-${headerSize.value}`]: true,
}))

function updateViewportWidth() {
  viewportWidth.value = window.innerWidth
}

async function handleTipClick(tip: string) {
  searchQuery.value = tip
  await navigateToSearch(tip)
}

async function handleSearch(query: string) {
  searchQuery.value = query
  await navigateToSearch(query)
}

function handleAddPhoto() {
  if (viewportWidth.value < 1024) {
    imageSearch.fairMobileImageFlowEntry = 'camera'
    imageSearch.isFairMobileImageFlowOpen = true
    return
  }

  imageSearch.fairDesktopImageFlowEntry = 'upload'
  imageSearch.isFairDesktopImageFlowOpen = true
}

function handleImageFile(file: File) {
  imageSearch.pendingImageFile = file

  if (viewportWidth.value < 1024) {
    imageSearch.fairMobileImageFlowEntry = 'camera'
    imageSearch.isFairMobileImageFlowOpen = true
    return
  }

  imageSearch.fairDesktopImageFlowEntry = 'upload'
  imageSearch.isFairDesktopImageFlowOpen = true
}

function handleEditPhoto() {
  if (viewportWidth.value < 1024) {
    imageSearch.fairMobileImageFlowEntry = 'crop'
    imageSearch.isFairMobileImageFlowOpen = true
    return
  }

  imageSearch.fairDesktopImageFlowEntry = 'crop'
  imageSearch.isFairDesktopImageFlowOpen = true
}

function handleDeletePhoto() {
  imageSearch.clearFairImageSearch()
}

function handleOpenSearchTutorial() {
  isSearchTutorialOpen.value = true
}

onMounted(() => {
  updateViewportWidth()
  window.addEventListener('resize', updateViewportWidth, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewportWidth)
})
</script>

<template>
  <section
    :class="rootClass"
    data-figma="1:8847"
  >
    <div
      class="fair-home-hero__bg"
      aria-hidden="true"
    >
      <img
        class="fair-home-hero__bg-img"
        :src="FAIR_HOME_HERO_BG_URL"
        alt=""
        fetchpriority="high"
        decoding="async"
      >
    </div>

    <div class="fair-home-hero__body">
      <div class="fair-home-hero__left">
        <h1 class="fair-home-hero__title">
          {{ title }}
        </h1>

        <div class="fair-home-hero__search-tip">
          <FairSearchBar
            v-model="searchQuery"
            class="fair-home-hero__search"
            type="main-page"
            :device="searchDevice"
            :placeholder="searchPlaceholder"
            :search-label="searchButtonLabel"
            :add-photo-label="addPhotoLabel"
            :delete-photo-label="deletePhotoLabel"
            :history-title="historyTitle"
            :brands-title="brandsTitle"
            :photo-url="photoUrl"
            :history="history"
            :suggestions="suggestions"
            :brands="brands"
            :suggestions-pending="suggestionsPending"
            @search="handleSearch"
            @add-photo="handleAddPhoto"
            @delete-photo="handleDeletePhoto"
            @edit-photo="handleEditPhoto"
            @image-file="handleImageFile"
            @select-history="handleSelectHistory"
            @select-suggestion="handleSelectSuggestion"
            @remove-history="handleRemoveHistory"
          />

          <FairAiTip
            v-if="isCompact"
            class="fair-home-hero__ai-tip"
            :label="aiTipLabel"
            :colored="false"
            :show-button="false"
            @click="handleOpenSearchTutorial"
          />
        </div>

        <div class="fair-home-hero__tips">
          <button
            v-for="(tip, index) in tips"
            :key="`${tip}-${index}`"
            type="button"
            class="fair-home-hero__tip"
            @click="handleTipClick(tip)"
          >
            {{ tip }}
          </button>
        </div>
      </div>

      <div
        v-if="!isCompact"
        class="fair-home-hero__banner"
      >
        <FairHomeHeroBanner
          v-model:page="bannerPage"
        />
      </div>
    </div>

    <FairHomeSearchTutorialSheet
      v-if="isSearchTutorialOpen"
      v-model:visible="isSearchTutorialOpen"
    />
  </section>
</template>
